import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";

/**
 * Independence Day banner — renders only on 15 August.
 * Dismissal is persisted per-year so returning users see it fresh next year.
 * Tricolour bar: saffron → white → green horizontal gradient, with a subtle
 * Ashoka-chakra tinted centre. Sits above the sticky navbar and scrolls
 * naturally out of view when the user scrolls.
 */
export default function IndependenceDayBanner() {
  const today = new Date();
  const isIndependenceDay = today.getMonth() === 7 && today.getDate() === 15; // Aug = 7 (0-indexed)
  const yearKey = `inprop.banner.ind-day.${today.getFullYear()}.dismissed`;

  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(yearKey) === "1";
    } catch (_e) {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) {
      try { window.localStorage.setItem(yearKey, "1"); } catch (_e) { /* noop */ }
    }
  }, [dismissed, yearKey]);

  if (!isIndependenceDay || dismissed) return null;

  return (
    <div
      className="relative w-full overflow-hidden"
      data-testid="independence-day-banner"
      role="region"
      aria-label="Independence Day promotion"
    >
      {/* Tricolor gradient — saffron, white, green */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(90deg, #FF9933 0%, #FF9933 24%, #FFFFFF 45%, #FFFFFF 55%, #138808 76%, #138808 100%)",
        }}
      />
      {/* Ashoka-chakra hint in the white middle band */}
      <div
        className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-20 flex items-center justify-center pointer-events-none"
        aria-hidden="true"
      >
        <div className="w-6 h-6 rounded-full border-2 border-[#000080]/40 relative">
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={i}
              className="absolute left-1/2 top-1/2 w-[1px] h-3 bg-[#000080]/40 origin-top"
              style={{ transform: `translate(-50%, 0) rotate(${i * 30}deg)` }}
            />
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-2.5 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0 flex items-center gap-2 md:gap-3 text-sg-navy text-xs md:text-sm">
          <span className="text-base md:text-lg leading-none" aria-hidden="true">🇮🇳</span>
          <div className="font-semibold truncate md:whitespace-normal">
            <span className="hidden md:inline">Happy 79th Independence Day — </span>
            <span className="md:hidden">Happy 79th I-Day — </span>
            <span className="font-normal text-sg-navy/80">Build your independent property business.</span>
          </div>
          <Link
            to="/subscribe"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-sg-navy text-white text-xs px-3 py-1 font-semibold whitespace-nowrap hover:bg-sg-navy-700 transition-colors ml-2"
            data-testid="ind-day-cta"
          >
            Join for ₹999/month <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {/* Mobile mini-CTA */}
        <Link
          to="/subscribe"
          className="md:hidden inline-flex items-center gap-1 rounded-full bg-sg-navy text-white text-[11px] px-2.5 py-1 font-semibold whitespace-nowrap"
          data-testid="ind-day-cta-mobile"
        >
          ₹999/mo <ArrowRight className="w-3 h-3" />
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 w-7 h-7 rounded-full bg-white/60 hover:bg-white text-sg-navy flex items-center justify-center transition-colors"
          aria-label="Dismiss Independence Day banner"
          data-testid="ind-day-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
